import React, { useRef, useState, useEffect } from 'react';
export type CardStyle =
    | 'background_image'
    | 'icon_color'
    | 'standard'
    | 'minimal'
    | 'large_feature';

export type GridLayout =
    | 'grid'
    | 'horizontal_scroll'
    | 'pill_list';

export interface CardItem {
    id: string;
    title: string;
    subtitle?: string;
    description?: string;
    thumbnail?: string;
    backgroundImage?: string;
    icon?: string;
    color?: string;
    logo?: string;
    videoCount?: number;
    duration?: string;
    views?: number;
    channelName?: string;
    badge?: string;
}

export interface CardGridProps {
    items: CardItem[];
    cardStyle: CardStyle;
    gridLayout: GridLayout;
    onItemClick?: (id: string) => void;
    showViewCount?: boolean;
    showDuration?: boolean;
    showDescription?: boolean;
    showCount?: boolean;
    showBrowseButton?: boolean;
    columns?: number;
    primaryColor?: string;
    primaryTextColor?: string;
    cardRadius?: string;
    fontFamily?: string;
}

// HELPERS

function formatViews(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return n.toString();
}


// INDIVIDUAL CARD RENDERERS

function BackgroundImageCard({
    item, onItemClick, showCount, showBrowseButton, showDescription, primary, radius, font,
}: {
    item: CardItem;
    onItemClick?: (id: string) => void;
    showCount?: boolean;
    showBrowseButton?: boolean;
    showDescription?: boolean;
    primary: string;
    radius: string;
    font: string;
}) {
    const img = item.backgroundImage || item.thumbnail;
    return (
        <div
            className="cg-bg-card"
            style={{ '--card-radius': radius, '--font': font } as React.CSSProperties}
            onClick={() => onItemClick?.(item.id)}
        >
            {img
                ? <img src={img} alt={item.title} className="cg-bg-img" />
                : <div className="cg-bg-placeholder" style={{ background: item.color || '#1e3a5c' }} />
            }
            <div className="cg-bg-overlay" />
            {showBrowseButton && (
                <button
                    className="cg-browse-btn"
                    style={{ '--primary': primary } as React.CSSProperties}
                    onClick={e => { e.stopPropagation(); onItemClick?.(item.id); }}
                >
                    Browse
                </button>
            )}
            {item.badge && <span className="cg-badge">{item.badge}</span>}
            <div className="cg-bg-body">
                {item.subtitle && <p className="cg-bg-eyebrow">{item.subtitle}</p>}
                <h3 className="cg-bg-name">{item.title}</h3>
                {showDescription && item.description && (
                    <p className="cg-bg-desc">{item.description}</p>
                )}
                {showCount && item.videoCount !== undefined && (
                    <span className="cg-bg-count">
                        <svg width="11" height="11" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zm12.553 1.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                        </svg>
                        {item.videoCount} videos
                    </span>
                )}
            </div>
        </div>
    );
}

function IconColorCard({
    item, onItemClick, showCount, radius, font,
}: {
    item: CardItem;
    onItemClick?: (id: string) => void;
    showCount?: boolean;
    radius: string;
    font: string;
}) {
    return (
        <div
            className="cg-icon-card"
            style={{ '--card-radius': radius, '--font': font } as React.CSSProperties}
            onClick={() => onItemClick?.(item.id)}
        >
            <div
                className="cg-icon-thumb"
                style={{ background: item.color || '#6366f1' }}
            >
                {item.thumbnail
                    ? <><img src={item.thumbnail} alt={item.title} /><div className="cg-icon-img-overlay" /></>
                    : item.icon
                        ? <span className="cg-icon-emoji">{item.icon}</span>
                        : <span className="cg-icon-emoji">📁</span>
                }
            </div>
            <div className="cg-icon-body">
                <p className="cg-icon-name">{item.title}</p>
                {showCount && item.videoCount !== undefined && (
                    <p className="cg-icon-count">{item.videoCount} items</p>
                )}
            </div>
        </div>
    );
}

function StandardCard({
    item, onItemClick, showViewCount, showDuration, showDescription,
}: {
    item: CardItem;
    onItemClick?: (id: string) => void;
    showViewCount?: boolean;
    showDuration?: boolean;
    showDescription?: boolean;
    radius: string;
    font: string;
}) {
    return (
        <div
            className="cg-std-card"
            onClick={() => onItemClick?.(item.id)}
        >
            <div className="cg-std-thumb">
                {item.thumbnail
                    ? <img src={item.thumbnail} alt={item.title} loading="lazy" />
                    : <div className="cg-std-placeholder" />
                }
                {showDuration && item.duration && (
                    <span className="cg-std-dur">{item.duration}</span>
                )}
                {item.badge && <span className="cg-badge cg-badge-tl">{item.badge}</span>}
            </div>
            <div className="cg-std-body">
                <h3 className="cg-std-title">{item.title}</h3>
                {showDescription && item.description && (
                    <p className="cg-std-desc">{item.description}</p>
                )}
                <div className="cg-std-meta">
                    {item.channelName && <span>{item.channelName}</span>}
                    {item.channelName && showViewCount && item.views && (
                        <span className="cg-meta-dot" />
                    )}
                    {showViewCount && item.views && (
                        <span>{formatViews(item.views)} views</span>
                    )}
                </div>
            </div>
        </div>
    );
}

function MinimalCard({
    item, onItemClick, showCount,
}: {
    item: CardItem;
    onItemClick?: (id: string) => void;
    showCount?: boolean;
    font: string;
}) {
    return (
        <div
            className="cg-min-row"
            onClick={() => onItemClick?.(item.id)}
        >
            {item.color && (
                <span className="cg-min-dot" style={{ background: item.color }} />
            )}
            {item.icon && <span className="cg-min-icon">{item.icon}</span>}
            <div className="cg-min-text">
                <span className="cg-min-name">{item.title}</span>
                {item.subtitle && <span className="cg-min-sub">{item.subtitle}</span>}
            </div>
            <div className="cg-min-right">
                {showCount && item.videoCount !== undefined && (
                    <span className="cg-min-count">{item.videoCount}</span>
                )}
                <svg className="cg-min-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
            </div>
        </div>
    );
}

function LargeFeatureCard({
    item, onItemClick, showViewCount, showDuration, showDescription, primary,
}: {
    item: CardItem;
    onItemClick?: (id: string) => void;
    showViewCount?: boolean;
    showDuration?: boolean;
    showDescription?: boolean;
    primary: string;
    radius: string;
    font: string;
}) {
    return (
        <div
            className="cg-lf-card"
            style={{ '--primary': primary } as React.CSSProperties}
            onClick={() => onItemClick?.(item.id)}
        >
            <div className="cg-lf-thumb">
                {item.thumbnail
                    ? <img src={item.thumbnail} alt={item.title} loading="lazy" />
                    : <div className="cg-lf-placeholder" style={{ background: item.color || '#1e3a5c' }} />
                }
                <div className="cg-lf-overlay" />
            </div>
            <div className="cg-lf-body">
                {item.channelName && (
                    <span className="cg-lf-channel">{item.channelName}</span>
                )}
                <h3 className="cg-lf-title">{item.title}</h3>
                {showDescription && item.description && (
                    <p className="cg-lf-desc">{item.description}</p>
                )}
                <div className="cg-lf-meta">
                    {showDuration && item.duration && (
                        <span className="cg-lf-meta-item">
                            <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            {item.duration}
                        </span>
                    )}
                    {showViewCount && item.views && (
                        <span className="cg-lf-meta-item">{formatViews(item.views)} views</span>
                    )}
                </div>
                <button className="cg-lf-watch-btn">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                    Watch Now
                </button>
            </div>
        </div>
    );
}

function PillChip({
    item, onItemClick, primary,
}: {
    item: CardItem;
    onItemClick?: (id: string) => void;
    primary: string;
    font: string;
}) {
    return (
        <button
            className="cg-pill"
            style={{ '--primary': primary } as React.CSSProperties}
            onClick={() => onItemClick?.(item.id)}
        >
            {item.color && <span className="cg-pill-dot" style={{ background: item.color }} />}
            {item.icon && <span>{item.icon}</span>}
            {item.title}
        </button>
    );
}


// MAIN CARD GRID COMPONENT

export const CardGrid: React.FC<CardGridProps> = ({
    items,
    cardStyle,
    gridLayout,
    onItemClick,
    showViewCount = false,
    showDuration = false,
    showDescription = false,
    showCount = true,
    showBrowseButton = true,
    columns,
    primaryColor = '#00897b',
    cardRadius = '10px',
    fontFamily = "'DM Sans', sans-serif",
}) => {
    const getMinCardWidth = () => {
        switch (cardStyle) {
            case 'background_image': return '280px';
            case 'large_feature': return '300px';
            case 'icon_color': return '130px';
            case 'standard': return '200px';
            default: return '160px';
        }
    };

    const colCount = columns ?? (
        cardStyle === 'icon_color' ? 6
            : cardStyle === 'background_image' ? 3
                : cardStyle === 'large_feature' ? 3
                    : 4
    );

    const cardProps = { primary: primaryColor, radius: cardRadius, font: fontFamily };

    const renderCard = (item: CardItem) => {
        switch (cardStyle) {
            case 'background_image':
                return <BackgroundImageCard key={item.id} item={item} onItemClick={onItemClick} showCount={showCount} showBrowseButton={showBrowseButton} showDescription={showDescription} {...cardProps} />;
            case 'icon_color':
                return <IconColorCard key={item.id} item={item} onItemClick={onItemClick} showCount={showCount} radius={cardRadius} font={fontFamily} />;
            case 'standard':
                return <StandardCard key={item.id} item={item} onItemClick={onItemClick} showViewCount={showViewCount} showDuration={showDuration} showDescription={showDescription} radius={cardRadius} font={fontFamily} />;
            case 'minimal':
                return <MinimalCard key={item.id} item={item} onItemClick={onItemClick} showCount={showCount} font={fontFamily} />;
            case 'large_feature':
                return <LargeFeatureCard key={item.id} item={item} onItemClick={onItemClick} showViewCount={showViewCount} showDuration={showDuration} showDescription={showDescription} {...cardProps} />;
            default:
                return null;
        }
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Syne:wght@700;800&display=swap');

                .cg-grid {
                    display: grid;
                    grid-template-columns: repeat(${colCount}, minmax(${getMinCardWidth()}, 1fr));
                    gap: 16px;
                }
                @media (max-width: 1024px) { .cg-grid { grid-template-columns: repeat(auto-fill, minmax(${getMinCardWidth()}, 1fr)); } }
                @media (max-width: 640px)  { .cg-grid { grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 10px; } }

                .cg-pills { display: flex; flex-wrap: wrap; gap: 8px; }

                .cg-bg-card {
                    position: relative; height: 200px;
                    border-radius: var(--card-radius, 10px); overflow: hidden;
                    cursor: pointer;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    transition: transform .22s, box-shadow .22s;
                    font-family: var(--font);
                }
                .cg-bg-card:hover { transform: translateY(-4px); box-shadow: 0 10px 28px rgba(0,0,0,0.18); }
                .cg-bg-img {
                    position: absolute; inset: 0;
                    width: 100%; height: 100%; object-fit: cover;
                    transition: transform .35s;
                }
                .cg-bg-card:hover .cg-bg-img { transform: scale(1.06); }
                .cg-bg-placeholder { position: absolute; inset: 0; }
                .cg-bg-overlay {
                    position: absolute; inset: 0;
                    background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.1) 55%, transparent 100%);
                }
                .cg-bg-body { position: absolute; bottom: 0; left: 0; right: 0; padding: 14px 16px; }
                .cg-bg-eyebrow {
                    font-size: 10px; font-weight: 700; letter-spacing: 0.08em;
                    text-transform: uppercase; color: rgba(255,255,255,0.65);
                    margin: 0 0 3px;
                }
                .cg-bg-name {
                    font-family: 'Syne', sans-serif;
                    font-size: 17px; font-weight: 700; color: #fff;
                    margin: 0 0 4px; line-height: 1.2;
                }
                .cg-bg-desc {
                    font-size: 12px; color: rgba(255,255,255,0.72);
                    margin: 0 0 6px; line-height: 1.4;
                    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
                }
                .cg-bg-count {
                    display: inline-flex; align-items: center; gap: 4px;
                    font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.65);
                }
                .cg-browse-btn {
                    position: absolute; top: 12px; right: 12px;
                    padding: 4px 12px;
                    background: rgba(255,255,255,0.15);
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 100px; color: #fff;
                    font-size: 11px; font-weight: 600;
                    cursor: pointer; backdrop-filter: blur(6px);
                    font-family: var(--font);
                    transition: background .15s;
                }
                .cg-browse-btn:hover { background: rgba(255,255,255,0.28); }

                .cg-icon-card {
                    cursor: pointer; text-align: center;
                    border-radius: var(--card-radius, 10px); overflow: hidden;
                    background: #fff; border: 1px solid #e5e7eb;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
                    transition: transform .2s, box-shadow .2s;
                    font-family: var(--font);
                }
                .cg-icon-card:hover { transform: translateY(-3px); box-shadow: 0 6px 20px rgba(0,0,0,0.1); }
                .cg-icon-thumb {
                    aspect-ratio: 1; display: flex;
                    align-items: center; justify-content: center;
                    overflow: hidden; position: relative;
                }
                .cg-icon-thumb img {
                    position: absolute; inset: 0;
                    width: 100%; height: 100%; object-fit: cover;
                    transition: transform .3s;
                }
                .cg-icon-card:hover .cg-icon-thumb img { transform: scale(1.07); }
                .cg-icon-img-overlay {
                    position: absolute; inset: 0;
                    background: rgba(0,0,0,0.15);
                    transition: background .2s;
                }
                .cg-icon-card:hover .cg-icon-img-overlay { background: rgba(0,0,0,0.06); }
                .cg-icon-emoji { font-size: 34px; line-height: 1; position: relative; }
                .cg-icon-body { padding: 9px 8px 11px; }
                .cg-icon-name {
                    font-size: 12.5px; font-weight: 700; color: #111827;
                    margin: 0 0 2px; line-height: 1.3;
                }
                .cg-icon-count { font-size: 11px; color: #6b7280; margin: 0; }

                .cg-std-card {
                    cursor: pointer;
                    border-radius: 10px; overflow: hidden;
                    background: #fff; border: 1px solid #e5e7eb;
                    box-shadow: 0 1px 4px rgba(0,0,0,0.07);
                    transition: transform .2s, box-shadow .2s;
                }
                .cg-std-card:hover { transform: translateY(-3px); box-shadow: 0 6px 20px rgba(0,0,0,0.1); }
                .cg-std-thumb {
                    position: relative; aspect-ratio: 16/9;
                    overflow: hidden; background: #e5e7eb;
                }
                .cg-std-thumb img { width: 100%; height: 100%; object-fit: cover; transition: transform .3s; }
                .cg-std-card:hover .cg-std-thumb img { transform: scale(1.05); }
                .cg-std-placeholder { width: 100%; height: 100%; background: #d1d5db; }
                .cg-std-dur {
                    position: absolute; bottom: 5px; right: 6px;
                    padding: 2px 6px; background: rgba(0,0,0,0.75); color: #fff;
                    font-size: 10.5px; font-weight: 700; border-radius: 4px;
                }
                .cg-std-body { padding: 10px 12px 12px; }
                .cg-std-title {
                    font-size: 13px; font-weight: 700; color: #111827;
                    margin: 0 0 4px; line-height: 1.35;
                    display: -webkit-box; -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical; overflow: hidden;
                }
                .cg-std-card:hover .cg-std-title { color: var(--primary, #00897b); }
                .cg-std-desc {
                    font-size: 12px; color: #6b7280; line-height: 1.5;
                    margin: 0 0 4px;
                    display: -webkit-box; -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical; overflow: hidden;
                }
                .cg-std-meta {
                    display: flex; align-items: center; gap: 8px;
                    font-size: 11.5px; color: #6b7280; flex-wrap: wrap;
                }
                .cg-meta-dot { width: 3px; height: 3px; border-radius: 50%; background: #d1d5db; }

                .cg-min-row {
                    display: flex; align-items: center; gap: 12px;
                    padding: 11px 14px; border-radius: 8px;
                    cursor: pointer; transition: background .15s;
                }
                .cg-min-row:hover { background: #f9fafb; }
                .cg-min-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
                .cg-min-icon { font-size: 18px; flex-shrink: 0; }
                .cg-min-text { flex: 1; min-width: 0; }
                .cg-min-name { font-size: 14px; font-weight: 600; color: #111827; display: block; }
                .cg-min-sub { font-size: 12px; color: #6b7280; display: block; margin-top: 1px; }
                .cg-min-right {
                    display: flex; align-items: center; gap: 8px;
                    flex-shrink: 0; color: #9ca3af;
                }
                .cg-min-count {
                    font-size: 12px; font-weight: 600; color: #6b7280;
                    background: #f3f4f6; border-radius: 100px;
                    padding: 2px 8px;
                }
                .cg-min-arrow { width: 14px; height: 14px; }

                .cg-lf-card {
                    position: relative;
                    border-radius: 10px; overflow: hidden;
                    cursor: pointer; height: 340px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    transition: transform .22s, box-shadow .22s;
                }
                .cg-lf-card:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(0,0,0,0.18); }
                .cg-lf-thumb { position: absolute; inset: 0; }
                .cg-lf-thumb img { width: 100%; height: 100%; object-fit: cover; transition: transform .35s; }
                .cg-lf-card:hover .cg-lf-thumb img { transform: scale(1.05); }
                .cg-lf-placeholder { width: 100%; height: 100%; }
                .cg-lf-overlay {
                    position: absolute; inset: 0;
                    background: linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 55%, transparent 100%);
                }
                .cg-lf-body {
                    position: absolute; bottom: 0; left: 0; right: 0; padding: 20px;
                }
                .cg-lf-channel {
                    display: inline-block;
                    font-size: 10px; font-weight: 700; letter-spacing: 0.08em;
                    text-transform: uppercase; color: var(--primary, #00897b);
                    margin-bottom: 6px;
                }
                .cg-lf-title {
                    font-family: 'Syne', sans-serif;
                    font-size: 20px; font-weight: 800; color: #fff;
                    margin: 0 0 6px; line-height: 1.2;
                }
                .cg-lf-desc {
                    font-size: 13px; color: rgba(255,255,255,0.75);
                    margin: 0 0 10px; line-height: 1.5;
                    display: -webkit-box; -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical; overflow: hidden;
                }
                .cg-lf-meta { display: flex; gap: 14px; margin-bottom: 12px; }
                .cg-lf-meta-item {
                    display: flex; align-items: center; gap: 4px;
                    font-size: 12px; color: rgba(255,255,255,0.65); font-weight: 500;
                }
                .cg-lf-watch-btn {
                    display: inline-flex; align-items: center; gap: 6px;
                    padding: 7px 18px;
                    background: var(--primary, #00897b); color: #fff;
                    font-size: 12.5px; font-weight: 700;
                    border-radius: 100px; border: none; cursor: pointer;
                    transition: filter .15s;
                }
                .cg-lf-watch-btn:hover { filter: brightness(1.1); }

                .cg-pill {
                    display: inline-flex; align-items: center; gap: 6px;
                    padding: 7px 16px; border-radius: 100px;
                    font-size: 13px; font-weight: 600;
                    border: 1.5px solid #e5e7eb;
                    background: #fff; color: #111827;
                    cursor: pointer;
                    transition: all .18s;
                }
                .cg-pill:hover {
                    border-color: var(--primary, #00897b);
                    color: var(--primary, #00897b);
                }
                .cg-pill-dot { width: 8px; height: 8px; border-radius: 50%; }

                .cg-badge {
                    position: absolute; top: 10px; left: 10px;
                    padding: 3px 8px; border-radius: 4px;
                    background: var(--primary, #00897b); color: #fff;
                    font-size: 10.5px; font-weight: 700;
                }
                .cg-badge-tl { top: 8px; left: 8px; }
            `}</style>

            {gridLayout === 'pill_list' && (
                <div className="cg-pills">
                    {items.map(item => (
                        <PillChip key={item.id} item={item} onItemClick={onItemClick} primary={primaryColor} font={fontFamily} />
                    ))}
                </div>
            )}

            {gridLayout === 'grid' && (
                <div className="cg-grid">
                    {items.map(item => renderCard(item))}
                </div>
            )}
        </>
    );
};

export default CardGrid;